import { useState } from 'react';
import { Accordion } from 'react-accessible-accordion';
import isEmpty from 'lodash.isempty';
import slugify from 'slugify';

// components
import OKVTilesView from './okv-tile/okv-tiles-view';
import { AccordionContent } from '../Accordion/AccordionContent';
import { Title } from '../Title/Title';
import { TitleFormats } from '../Title/title.types';

// styles
import styles from './online-catalog-assets.module.scss';

// types
import type { OKVJsonContent, BackgroundColor } from './types';

// utils
import { useIsMobile } from 'components/App/SizeProvider';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useSectionId } from 'utils/hooks/use-section-id';
import { previewId } from 'utils/preview-id';
import { useFetchAssets } from './useFetchAssets';

export interface OnlineCatalogAssetsProps {
  title: string;
  anchor: string;
  subtitle: string;
  contentIndex: number;
  titleInAnchor: boolean;
  catalogName: string;
  isSectionReference: boolean;
  data: OKVJsonContent[];
  backgroundColor: BackgroundColor;
  hideCount: boolean;
  carousel: boolean;
}

export function OnlineCatalogAssets({
  title,
  anchor,
  subtitle,
  contentIndex,
  titleInAnchor,
  catalogName,
  isSectionReference,
  data,
  backgroundColor,
  hideCount,
  carousel,
}: Readonly<OnlineCatalogAssetsProps>) {
  const isMobile = useIsMobile();
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const { data: okvTiles } = useFetchAssets({ data, hideCount });
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const isAccordionView = data.length > 1;

  const handleAccordionOnClick = (item) => {
    const itemIndex = item.currentTarget.dataset.index;
    if (activeIndex !== itemIndex) {
      setActiveIndex(itemIndex);
    } else {
      setActiveIndex(undefined);
    }
  };

  const getDisplayOption = getDisplayOptionBuilder(carousel, isMobile);

  const renderOKVAccordion = () => (
    <Accordion className="c-accordion" allowZeroExpanded>
      {okvTiles.map((accordionRow, index) => {
        // define content for each row of the accordion
        const content = (
          <div
            key={`content-${index}`}
            className={classNameBuilder(styles.accWrapper, styles[`${backgroundColor}Background`])}
          >
            <OKVTilesView
              componentName="subcategory-tiles"
              displayOption={getDisplayOption(accordionRow.items)}
              backgroundColor={backgroundColor}
              items={accordionRow.items}
              slidesToScroll={3}
              isAssortment
            />
          </div>
        );

        return (
          <div key={`accordion-${index}`} {...previewId(isSectionReference ? '' : `#${index}`)}>
            <AccordionContent
              uuid={slugify(accordionRow.title!)}
              key={accordionRow.title!}
              title={accordionRow.title}
              content={content}
              backgroundColor={backgroundColor}
              index={index}
              activeIndex={activeIndex}
              isOKV={true}
              handleClick={handleAccordionOnClick}
            />
          </div>
        );
      })}
    </Accordion>
  );

  const renderOKVCarousel = () =>
    !isEmpty(okvTiles[0]?.items) && (
      <OKVTilesView
        componentName="product-tiles"
        items={okvTiles[0].items}
        displayOption={getDisplayOption(okvTiles[0].items)}
        slidesToScroll={3}
        hideCount={hideCount}
      />
    );

  return (
    <div
      className={classNameBuilder(
        !isAccordionView && styles[`${backgroundColor}Background`],
        styles.okvContainer,
        styles.isAssortment,
      )}
    >
      <div
        className="grid-container"
        id={sectionId}
        {...previewId(catalogName && !isSectionReference ? `#${catalogName}` : undefined)}
      >
        <Title
          title={title}
          subtitle={subtitle}
          Format={TitleFormats.h2}
          className={styles.assetHeadline}
        />
        {isAccordionView ? renderOKVAccordion() : renderOKVCarousel()}
      </div>
    </div>
  );
}

// closure function
// initial the "static" props are passed (isCarousel & isMobile)
// then, when the function is used the (different) items are passed
const getDisplayOptionBuilder =
  (isCarousel: boolean, isMobile: boolean) =>
  (items: any): 'carousel' | '3-per-row' => {
    return (items.length > 2 && isCarousel) || isMobile ? 'carousel' : '3-per-row';
  };
